import { FallbackError } from "@core/domain/errors/fallback.error";
import { convertEmptyToUndefined } from "@core/domain/types/undefinable.type";
import { EditCost } from "@entity/domain/models/cost/edit-cost.model";
import { CostFormValuesValidated } from "@entity/presentation/component/cost-form/cost-form.component";
import { inject, injectable } from "inversify";
import { EditCostBody } from "../../dto/cost/edit-cost.body";
import { IVATypeEnumDto } from "../../dto/iva-type.dto";
import { IdentificationEnumMapper } from "../deposit/identification-enum.mapper";
import { CostTypeEnumMapper } from "./cost-type-enum.mapper";
import { IRPFTypeEnumMapper } from "./irpf-type-enum.mapper";
import { IVAEnumMapper } from "./iva-enum.mapper";
import { PaymentMethodTypeEnumMapper } from "./payment-method-type.mapper";

@injectable()
export class EditCostMapper {
    constructor(
        @inject(PaymentMethodTypeEnumMapper)
        private readonly paymentMethodTypeEnumMapper: PaymentMethodTypeEnumMapper,
        @inject(IdentificationEnumMapper)
        private readonly identificationEnumMapper: IdentificationEnumMapper,
        @inject(CostTypeEnumMapper)
        private readonly costTypeEnumMapper: CostTypeEnumMapper,
        @inject(IVAEnumMapper)
        private readonly ivaEnumMapper: IVAEnumMapper,
        @inject(IRPFTypeEnumMapper)
        private readonly irpfTypeEnumMapper: IRPFTypeEnumMapper,
    ) {}
    mapFromCostFormValues(
        costFormValuesValidated: CostFormValuesValidated,
        entityId: number,
        costId: number,
    ): EditCost {
        return new EditCost(
            costId,
            entityId,
            costFormValuesValidated.invoiceNum,
            costFormValuesValidated.concept,
            costFormValuesValidated.socialReason,
            costFormValuesValidated.identificationType,
            costFormValuesValidated.identification,
            costFormValuesValidated.costType,
            costFormValuesValidated.invoiceDate,
            costFormValuesValidated.paymentDate,
            costFormValuesValidated.paymentMethod,
            costFormValuesValidated.base,
            costFormValuesValidated.iva,
            0,
            costFormValuesValidated.totalCost,
            costFormValuesValidated.irpfType,
            costFormValuesValidated.otherCostType ?? undefined,
            undefined,
            costFormValuesValidated.documents[0],
            costFormValuesValidated.additionalExpensesToUpdate,
        );
    }

    mapToDto(editCost: EditCost): EditCostBody {
        if (
            !editCost.paymentMethod ||
            !editCost.irpfType ||
            !editCost.issuerIdentificationType
        ) {
            throw new FallbackError();
        }
        const identificationType = this.identificationEnumMapper.mapToDto(
            editCost.issuerIdentificationType,
        );

        const paymentMethod = this.paymentMethodTypeEnumMapper.mapToDto(
            editCost.paymentMethod,
        );

        const type = this.costTypeEnumMapper.mapToDto(editCost.type);

        const iva = this.ivaEnumMapper.mapToDto(editCost.iva);

        const irpf = this.irpfTypeEnumMapper.mapToDto(editCost.irpfType);

        return {
            id: editCost.id,
            number: editCost.invoiceNum,
            concept: editCost.concept,
            issuer_name: editCost.issuerName,
            issuer_identification_type: identificationType,
            issuer_identification_number: editCost.identificationNumber,
            type: type,
            others_type: editCost.othersType ?? "",
            issue_date: convertEmptyToUndefined(editCost.invoiceDate?.toISO()),
            payment_date: convertEmptyToUndefined(
                editCost.paymentDate?.toISO(),
            ),
            payment_method: convertEmptyToUndefined(paymentMethod),
            other_payment_method: editCost.otherPaymentMethod ?? "",
            base: editCost.base ?? 0,
            iva: iva ?? IVATypeEnumDto.Cero,
            total: convertEmptyToUndefined(editCost.total),
            discount: editCost.discount ?? 0,
            irpf_type: irpf,
            entity: editCost.entityId,
            document: editCost.document?.id,
        };
    }
}
